import { type CanonicalIcd10Code, parseCanonicalIcd10Code } from "./brands";
import { clinicalCodeCategory } from "./normalise";

export type PersonalHistoryConversion = { code: CanonicalIcd10Code; description: string };

type CategoryRange = readonly [from: string, to: string];

type ConversionRow = {
  ranges: readonly CategoryRange[];
  code: string;
  description: string;
};

// First match wins, so an exception sits above the range it carves out of.
const CONVERSION_ROWS: readonly ConversionRow[] = [
  { ranges: [["I21", "I21"]], code: "I25.2", description: "Old myocardial infarction" },
  {
    ranges: [["C15", "C26"]],
    code: "Z85.0",
    description: "Personal history of malignant neoplasm of digestive organs",
  },
  {
    ranges: [["C33", "C34"]],
    code: "Z85.1",
    description: "Personal history of malignant neoplasm of trachea, bronchus and lung",
  },
  {
    ranges: [
      ["C30", "C32"],
      ["C37", "C39"],
    ],
    code: "Z85.2",
    description: "Personal history of malignant neoplasm of other respiratory and intrathoracic organs",
  },
  { ranges: [["C50", "C50"]], code: "Z85.3", description: "Personal history of malignant neoplasm of breast" },
  {
    ranges: [["C51", "C63"]],
    code: "Z85.4",
    description: "Personal history of malignant neoplasm of genital organs",
  },
  {
    ranges: [["C64", "C68"]],
    code: "Z85.5",
    description: "Personal history of malignant neoplasm of urinary tract",
  },
  { ranges: [["C91", "C95"]], code: "Z85.6", description: "Personal history of leukaemia" },
  {
    ranges: [
      ["C81", "C90"],
      ["C96", "C96"],
    ],
    code: "Z85.7",
    description: "Personal history of other malignant neoplasms of lymphoid, haematopoietic and related tissues",
  },
  {
    ranges: [
      ["C00", "C14"],
      ["C40", "C49"],
      ["C69", "C79"],
      ["C97", "C97"],
    ],
    code: "Z85.8",
    description: "Personal history of malignant neoplasms of other organs and systems",
  },
  {
    ranges: [["C80", "C80"]],
    code: "Z85.9",
    description: "Personal history of malignant neoplasm, unspecified",
  },
  { ranges: [["D00", "D48"]], code: "Z86.0", description: "Personal history of other neoplasms" },
  {
    ranges: [
      ["A00", "B89"],
      ["B99", "B99"],
    ],
    code: "Z86.1",
    description: "Personal history of infectious and parasitic diseases",
  },
  {
    ranges: [["D50", "D89"]],
    code: "Z86.2",
    description: "Personal history of diseases of the blood and blood-forming organs and certain immune disorders",
  },
  {
    ranges: [["E00", "E90"]],
    code: "Z86.3",
    description: "Personal history of endocrine, nutritional and metabolic diseases",
  },
  { ranges: [["F10", "F19"]], code: "Z86.4", description: "Personal history of psychoactive substance abuse" },
  {
    ranges: [
      ["F00", "F09"],
      ["F20", "F99"],
    ],
    code: "Z86.5",
    description: "Personal history of other mental and behavioural disorders",
  },
  {
    ranges: [
      ["G00", "G99"],
      ["H00", "H95"],
    ],
    code: "Z86.6",
    description: "Personal history of diseases of the nervous system and sense organs",
  },
  {
    ranges: [["I00", "I99"]],
    code: "Z86.7",
    description: "Personal history of diseases of the circulatory system",
  },
  {
    ranges: [["J00", "J99"]],
    code: "Z87.0",
    description: "Personal history of diseases of the respiratory system",
  },
  { ranges: [["K00", "K93"]], code: "Z87.1", description: "Personal history of diseases of the digestive system" },
  {
    ranges: [["L00", "L99"]],
    code: "Z87.2",
    description: "Personal history of diseases of the skin and subcutaneous tissue",
  },
  {
    ranges: [["M00", "M99"]],
    code: "Z87.3",
    description: "Personal history of diseases of the musculoskeletal system and connective tissue",
  },
  {
    ranges: [["N00", "N99"]],
    code: "Z87.4",
    description: "Personal history of diseases of the genitourinary system",
  },
  {
    ranges: [["O00", "O99"]],
    code: "Z87.5",
    description: "Personal history of complications of pregnancy, childbirth and the puerperium",
  },
  {
    ranges: [["P00", "P96"]],
    code: "Z87.6",
    description: "Personal history of certain conditions arising in the perinatal period",
  },
  {
    ranges: [["Q00", "Q99"]],
    code: "Z87.7",
    description: "Personal history of congenital malformations, deformations and chromosomal abnormalities",
  },
  { ranges: [["S00", "T98"]], code: "Z87.8", description: "Personal history of other specified conditions" },
];

const brandedCode = (code: string): CanonicalIcd10Code => {
  const parsed = parseCanonicalIcd10Code(code);
  if (parsed === undefined) throw new Error(`personal history table holds a malformed code: ${code}`);
  return parsed;
};

const CONVERSIONS = CONVERSION_ROWS.map(({ ranges, code, description }) => ({
  ranges,
  conversion: { code: brandedCode(code), description } satisfies PersonalHistoryConversion,
}));

/**
 * The personal-history code a still-coded condition becomes once it is no longer current, or
 * `undefined` where the table has no row for the category (`D49`, `B90–B98`, chapters `R`, `U`,
 * `V–Y`). The table says where a condition goes, never whether it should: that is the caller's call.
 */
export const personalHistoryConversionFor = (code: string): PersonalHistoryConversion | undefined => {
  const canonical = parseCanonicalIcd10Code(code);
  if (canonical === undefined) return undefined;

  const category = clinicalCodeCategory(canonical);
  return CONVERSIONS.find(({ ranges }) => ranges.some(([from, to]) => category >= from && category <= to))?.conversion;
};
